import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, Box, Divider, Menu, MenuItem, PopoverOrigin } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import useTheme from "../../../../common/hooks/useTheme";
import { useAuth } from "../../../../providers/AuthProvider";
import { Theme } from "../../../../providers/ThemeProvider";
import { appendChatModeToPath } from "../../../chat/chatMode";

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
};

/**
 * The logged-in user's account dropdown: user info card, Account settings
 * (non-simple users) and Log out. Anchored top-right for the simple user
 * (Breadcrumb) and to the bottom sidebar avatar for affiliates
 * (PlatformSidebar).
 */
const UserAccountMenu = ({
  anchorEl,
  onClose,
  anchorOrigin = { vertical: "bottom", horizontal: "right" },
  transformOrigin = { vertical: "top", horizontal: "right" },
}: Props) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { AuthUser, AuthIsSimpleUser, AuthLogout } = useAuth();

  const getUserInitials = () => {
    if (AuthIsSimpleUser) {
      return `${AuthUser.firstname?.charAt(0) || ""}${
        AuthUser.lastname?.charAt(0) || ""
      }`.toUpperCase();
    }
    return AuthUser.name?.charAt(0)?.toUpperCase() || "";
  };

  const userName = AuthIsSimpleUser
    ? `${AuthUser.firstname || ""} ${AuthUser.lastname || ""}`.trim()
    : AuthUser.name || "";

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      sx={{
        "& .MuiPaper-root": {
          ...styles.paperContainer,
        },
      }}
    >
      <Box sx={{ padding: "12px" }}>
        {/* User info card */}
        <Box sx={styles.userSectionContainer(theme)}>
          <Avatar sx={styles.userSectionAvatar(theme)}>
            {getUserInitials()}
          </Avatar>
          <Box>
            <Box sx={styles.userSectionName(theme)}>{userName}</Box>
            <Box sx={styles.userSectionEmail(theme)}>
              {AuthUser.email || ""}
            </Box>
          </Box>
        </Box>
        {/*Account Settings*/}
        {!AuthIsSimpleUser && (
          <MenuItem
            onClick={() => {
              onClose();
              navigate(
                appendChatModeToPath("/client/settings", location.search)
              );
            }}
            sx={{
              padding: "8px",
              borderRadius: "8px",
              marginBottom: "8px",
              "&:hover": {
                backgroundColor: theme.colorTokens.N[40],
              },
            }}
          >
            <SettingsIcon
              sx={{
                fontSize: 14,
                marginRight: "8px",
                color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
              }}
            />
            <Box sx={styles.userSectionMenuItemLabel(theme)}>
              Account settings
            </Box>
          </MenuItem>
        )}
        <Divider sx={{ margin: "8px 0" }} />
        {/* Logout */}
        <MenuItem
          onClick={() => AuthLogout()}
          sx={styles.userSectionMenuItem(theme)}
        >
          <LogoutIcon
            sx={{
              fontSize: 16,
              marginRight: "10px",
              color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
            }}
          />
          <Box sx={styles.userSectionMenuItemLabel(theme)}>Log out</Box>
        </MenuItem>
      </Box>
    </Menu>
  );
};

export default UserAccountMenu;

const styles = {
  userSectionContainer: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.N[30],
    padding: "12px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "16px",
  }),
  userSectionAvatar: (theme: Theme) => ({
    width: 40,
    height: 40,
    backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
    fontSize: "20px",
    fontFamily: "var(--font-Bold)",
  }),
  userSectionName: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "16px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    lineHeight: "23px",
  }),
  userSectionEmail: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: "17px",
  }),
  userSectionMenuItem: (theme: Theme) => ({
    padding: "8px",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: theme.colorTokens.N[40],
    },
  }),
  userSectionMenuItemLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    flex: 1,
  }),
  paperContainer: {
    borderRadius: "8px",
    boxShadow: "var(--box-shadow-general)",
    maxWidth: "380px",
    maxHeight: "500px",
    minHeight: "40px",
  },
};
